import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { config } from "~/config.js";
import { api } from "~/lib/api.js";
import { getFlights, updateWebsocketEvent } from "~/redux/flights/flights.actions.js";

/**
 * Manages a WebSocket connection for live flight data.
 *
 * @param {object} options
 * @param {object|null} options.club            - Club object (needs schema_name)
 * @param {string|number} options.dayId         - The day id to connect for
 * @param {boolean} options.shouldConnect       - Gates whether the socket is active
 * @param {boolean} options.muteSound           - Suppress text-to-speech on incoming events
 *
 * @returns {{ connected: number }}  -1 = connecting, 1 = open, 0 = closed/error
 */
export function useWebsocket({ club, dayId, shouldConnect, muteSound }) {
    const [firstConnect, setFirstConnect] = useState(false);
    const [connected, setConnection] = useState(-1);
    const ws = useRef(null);
    const mounted = useRef(false);
    const dispatch = useDispatch();

    const setupNewWebsocket = useCallback(() => {
        try {
            if (!mounted.current || !dayId || !shouldConnect || !club?.schema_name) {
                console.log("Websocket: skipping setup (not mounted, no dayId, shouldConnect=false, or no club)");
                return;
            }

            // Close any existing live connection before opening a new one
            if (ws.current && (ws.current.readyState === 0 || ws.current.readyState === 1)) {
                console.log("Websocket: closing before reopening, readyState:", ws.current.readyState);
                try { ws.current.close(); } catch (e) { /* ignore */ }
            }

            setConnection(-1);
            ws.current = new WebSocket(
                `${config.ws_url}/flights/${club.schema_name}/${dayId}/${api.getToken()}`
            );
            console.log("Websocket: connecting to", ws.current.url);

            const openTimeout = setTimeout(() => {
                if (ws.current && ws.current.readyState !== 1) {
                    console.warn("Websocket: onopen not called after 5s, readyState:", ws.current.readyState);
                }
            }, 5000);

            ws.current.onopen = () => {
                clearTimeout(openTimeout);
                console.log("Websocket: connection opened for day", dayId);
                setConnection(1);
                if (!firstConnect) {
                    setFirstConnect(true);
                }

                // Fetch flights to cover any events missed while disconnected
                dispatch(getFlights({ dag_id: dayId, skipPending: true, origin: 'websocket_reconnect' }));
            };

            ws.current.onclose = () => {
                clearTimeout(openTimeout);
                console.log("Websocket: connection closed");
                setConnection(0);
                if (mounted.current && shouldConnect) {
                    console.log("Websocket: retrying in 5 seconds");
                    setTimeout(() => setupNewWebsocket(), 5000);
                }
            };

            ws.current.onerror = () => {
                clearTimeout(openTimeout);
                console.log("Websocket: error");
                setConnection(0);
            };

            ws.current.onmessage = (event) => {
                const message = JSON.parse(event.data);
                console.info("Websocket: received message", JSON.stringify(message, null, 2));
                dispatch(updateWebsocketEvent(message, muteSound));
            };
        } catch (e) {
            console.log("Websocket: error, retrying in 5 seconds", e);
            setConnection(0);
            setTimeout(() => setupNewWebsocket(), 5000);
        }
        // muteSound is intentionally excluded: it is only read inside onmessage which
        // closes over the latest value via the dispatch closure; adding it would cause
        // the socket to tear down on every mute toggle.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [club?.schema_name, dayId, shouldConnect, dispatch]);

    useEffect(() => {
        mounted.current = true;

        if (shouldConnect) {
            setupNewWebsocket();
        } else {
            // Caller disabled the connection — close any open socket cleanly
            if (ws.current && (ws.current.readyState === 0 || ws.current.readyState === 1)) {
                console.log("Websocket: shouldConnect=false, closing");
                ws.current.close();
            }
        }

        return () => {
            console.log("Websocket: cleaning up");
            mounted.current = false;
            if (ws.current && (ws.current.readyState === 0 || ws.current.readyState === 1)) {
                ws.current.close();
            }
        };
    }, [dayId, shouldConnect, setupNewWebsocket]);

    // When the browser comes back online, resync flights (missed events) and re-establish the socket
    useEffect(() => {
        if (!shouldConnect) return;

        const handleOnline = () => {
            if (!setFirstConnect) return; // skip if we've never connected before (e.g. first load while offline)
            console.log("Websocket: browser came online, resyncing flights and reconnecting");
            dispatch(getFlights({ dag_id: dayId, skipPending: true, origin: 'online' }));
        };

        window.addEventListener("online", handleOnline);
        return () => window.removeEventListener("online", handleOnline);
    }, [dayId, shouldConnect, dispatch, setupNewWebsocket]);

    // Periodic resync every 5 minutes to catch any missed events
    useEffect(() => {
        if (!shouldConnect) return;

        const interval = setInterval(() => {
            if (!setFirstConnect) return; // skip if we've never connected before (e.g. first load while offline)
            console.log("Websocket: periodic resync of flights");
            dispatch(getFlights({ dag_id: dayId, skipPending: true, origin: 'periodic' }));
        }, 4 * 60 * 1000);


        return () => clearInterval(interval);
    }, [dayId, shouldConnect, dispatch]);

    return { connected };
}
